import { useCallback, useEffect, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import { fetchWithAuth } from "@/lib/utils";
import i18n from "@/lib/i18n";

export interface AvailableSecret {
  path: string;
  name: string;
  description: string;
}

export interface StoredSecret {
  secret_name: string;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface UseUserSecretsReturn {
  availableSecrets: AvailableSecret[] | null;
  storedSecrets: StoredSecret[] | null;
  loading: boolean;
  error: string | null;
  saveSecret: (path: string, name: string, value: string) => Promise<boolean>;
  deleteSecret: (path: string, name: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

async function getResponseError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body === "object") {
      const message = body.detail ?? body.error ?? body.message;
      if (typeof message === "string") return message;
    }
  } catch {
    // Fall back to the HTTP status when the response does not contain JSON.
  }

  return `HTTP ${response.status}: ${response.statusText}`;
}

export function useUserSecrets(getToken: () => Promise<string>): UseUserSecretsReturn {
  const [availableSecrets, setAvailableSecrets] = useState<AvailableSecret[] | null>(null);
  const [storedSecrets, setStoredSecrets] = useState<StoredSecret[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [availableResponse, storedResponse] = await Promise.all([
        fetchWithAuth("/api/user-secrets/available", { headers: {} }, getToken),
        fetchWithAuth("/api/user-secrets", { headers: {} }, getToken),
      ]);

      if (!availableResponse.ok) throw new Error(await getResponseError(availableResponse));
      if (!storedResponse.ok) throw new Error(await getResponseError(storedResponse));

      const [available, stored] = await Promise.all([
        availableResponse.json(),
        storedResponse.json(),
      ]);
      setAvailableSecrets(available as AvailableSecret[]);
      setStoredSecrets(stored as StoredSecret[]);
    } catch (err: any) {
      setError(err?.message ?? i18n.t("connections:apiTokens.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const saveSecret = useCallback(
    async (path: string, name: string, value: string): Promise<boolean> => {
      setError(null);

      try {
        const response = await fetchWithAuth(
          "/api/user-secrets",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path, name, value, metadata: {} }),
          },
          getToken,
        );
        if (!response.ok) throw new Error(await getResponseError(response));

        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:apiTokens.errors.save"));
        return false;
      }
    },
    [getToken, refresh],
  );

  const deleteSecret = useCallback(
    async (path: string, name: string): Promise<boolean> => {
      setError(null);

      try {
        const query = new URLSearchParams({ path, name });
        const response = await fetchWithAuth(
          `/api/user-secrets?${query.toString()}`,
          { method: "DELETE", headers: {} },
          getToken,
        );
        if (!response.ok) throw new Error(await getResponseError(response));

        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:apiTokens.errors.remove"));
        return false;
      }
    },
    [getToken, refresh],
  );

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    availableSecrets,
    storedSecrets,
    loading,
    error,
    saveSecret,
    deleteSecret,
    refresh,
  };
}
